import React, { useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { HelpOutline, AccountCircle } from '@material-ui/icons';
import { useAppKeyWords, useWindowDimension } from 'hooks';
import Link from 'shared/link';
import {
    Collapse,
    DropdownMenu,
    DropdownToggle,
    Nav,
    NavbarBrand,
    NavbarToggler,
    NavItem,
    NavLink,
    Dropdown,
} from 'reactstrap';
import { hasOneOfRoles, hasProjectManagerReportRights } from 'utils/rightsUtil';
import { resetScheduler } from 'actions/schedulerActions';
import { getUrl } from 'utils/browserUtil';
import TrialItem from './trialItem';
import TimesheetItem from './timesheetItem';
import Help from './help';
import Env from './env';
import AddItem from './addItem';
import DeleteDummyDataButton from './deletedummydata';
import ReportItem from './reportItem';
import Profile from './profile';
import logo from 'assets/images/hubplanner_logo_mark_1.png';
import { NavBarFixed } from './styles';
import { STATUS_TRIAL } from 'enums/companyStatusEnum';
import { getProjects, selectDefaultVacationEvent } from '../../selectors/project';
import { Button, Layout } from '@hub-mono/ui';
import styled from 'styled-components';
import { getResources } from '../../selectors/resource';

const StyledNavbarBrandWrapper = styled(Layout)`
    min-height: 52px;
`;

const StyledImage = styled.img`
    border-radius: 100%;
    height: 26px;
    width: 26px;
`;

const StyledImgButton = styled(Button)`
    &&& {
        min-height: 32px;
        height: unset;
        padding: 2px 16px 2px 4px;
        border: 1px solid ${props => props.theme.designTokens.color.color.surfaces['surface-container-high'].value};
        background-color: ${props => props.theme.designTokens.color.color.surfaces['surface-container-high'].value};
        color: ${props => props.theme.designTokens.color.color.primary.primary.value};

        &:hover,
        &:active,
        &:focus {
            border: 1px solid ${props => props.theme.designTokens.color.color.surfaces['surface-container-high'].value};
            background-color: ${props => props.theme.designTokens.color.color.surfaces['surface-container-high'].value};

            &::before {
                content: '';
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                background-color: ${props =>
                    props.theme.designTokens.color.color['state-layers'].light['primary']['opacity-0.08'].value};
            }
        }
    }
`;

const StyledButton = styled(Button)`
    &&& {
        min-height: 32px;
        padding: 2px 16px;
        height: unset;
    }
`;

const Header = () => {
    const dispatch = useDispatch();

    const [collapsed, setCollapsed] = useState(false);
    const [isHelpMenuOpen, setHelpMenuOpen] = useState(false);
    const [isProfileOpen, setProfileOpen] = useState(false);
    const { windowHeight, windowWidth } = useWindowDimension();
    const company = useSelector(state => state.companyReducer.company);
    const { resourceRoleRights, isProjectManager } = useSelector(state => state.account);
    const defaultVacationEvent = useSelector(selectDefaultVacationEvent);
    const { vacationEventName } = useAppKeyWords();

    const resources = useSelector(getResources);
    const projects = useSelector(getProjects);

    const hasDummyData = useMemo(() => {
        return resources.some(resource => resource.dummy || projects.some(project => project.dummy));
    }, [resources, projects]);

    const toggleNavbar = () => {
        setCollapsed(prevState => !prevState);
    };

    const toggleProfile = () => {
        setProfileOpen(prevState => !prevState);
    };

    const toggleHelpMenu = () => {
        setHelpMenuOpen(prevState => !prevState);
    };

    const hasReportRights =
        hasOneOfRoles(resourceRoleRights, ['manageReports', 'manageMyReports']) ||
        hasProjectManagerReportRights({ resourceRoleRights, isProjectManager });

    return (
        <>
            <NavBarFixed color="" fixed="top" expand="lg">
                <StyledNavbarBrandWrapper vAlign="center">
                    <NavbarBrand href={getUrl(company.subdomain, '8090')} className="text-truncate">
                        <img src={logo} height="25" className="d-inline-block align-top" alt="hubplanner logo" />
                        {company.name}
                    </NavbarBrand>
                </StyledNavbarBrandWrapper>
                <Env />
                <Layout gap="--Spacing-100">
                    <CtaButtons company={company} />
                    <NavbarToggler onClick={toggleNavbar} />
                </Layout>
                <Collapse isOpen={collapsed} navbar>
                    <Nav className="ml-auto" navbar>
                        <NavItem>
                            <CtaButtons company={company} collapse />
                        </NavItem>
                        {hasDummyData && company.dummyDataDeleted !== true && <DeleteDummyDataButton />}
                        <AddItem />
                        <TrialItem companyStatus={company.status} trial={company.trial} />
                        <NavItem>
                            <Link
                                className="nav-link"
                                to="/scheduler"
                                onClick={() => dispatch(resetScheduler.request())}
                            >
                                Scheduler
                            </Link>
                        </NavItem>
                        <TimesheetItem />
                        <NavItem className="vacation-event">
                            <Link className="nav-link" to="/requests/my-vacations?subFlow=DASHBOARD&filter=ALL">
                                {defaultVacationEvent?.name ?? vacationEventName}
                            </Link>
                        </NavItem>
                        <NavItem>
                            <Link className="nav-link" to="/requests/my-resource-requests?subFlow=DASHBOARD&filter=ALL">
                                Requests
                            </Link>
                        </NavItem>
                        <NavItem>
                            <NavLink href="/dashboard">Dashboard</NavLink>
                        </NavItem>
                        <ReportItem />
                        <NavItem>
                            <Link className="nav-link" to="/settings/profile">
                                Settings
                            </Link>
                        </NavItem>
                        <NavItem>
                            <NavLink href="#" onClick={toggleHelpMenu}>
                                <HelpOutline fontSize="small" />
                            </NavLink>
                        </NavItem>
                        <Dropdown nav inNavbar={windowWidth < 1200} isOpen={isProfileOpen} toggle={toggleProfile}>
                            <DropdownToggle nav>
                                <AccountCircle fontSize="small" />
                            </DropdownToggle>
                            <DropdownMenu right className="rounded-0 p-0">
                                {isProfileOpen && <Profile />}
                            </DropdownMenu>
                        </Dropdown>
                    </Nav>
                </Collapse>
            </NavBarFixed>
            <Help
                windowHeight={windowHeight}
                windowWidth={windowWidth}
                companyStatus={company.status}
                hasReportAccess={hasReportRights}
                onClose={toggleHelpMenu}
                isOpen={isHelpMenuOpen}
            />
        </>
    );
};

const StyledLayout = styled(Layout)`
    height: 52px;
    @media (max-width: 1200px) {
        display: ${props => (props.collapse ? 'none' : 'flex')};
    }

    @media (min-width: 1201px) {
        min-width: 365px;
        display: ${props => (props.collapse ? 'flex' : 'none')};
    }
`;

const CtaButtons = ({ company, collapse }) => {
    if (STATUS_TRIAL.value !== company.status) {
        return null;
    }

    return (
        <StyledLayout
            gap="--Spacing-100"
            pt="--Spacing-50"
            vAlign="center"
            hAlign="flex-end"
            pr="--Spacing-50"
            collapse={collapse}
        >
            <StyledImgButton
                variant="text"
                href="https://meetings-eu1.hubspot.com/vjestica/milient-talk-to-an-expert"
                target="_blank"
            >
                <StyledImage src="https://hubplanner-static.s3.eu-west-1.amazonaws.com/img/Tom_Image.png" />
                Talk to an expert
            </StyledImgButton>
            <StyledButton
                endIcon="videocam"
                href="https://meetings-eu1.hubspot.com/vjestica/milient-talk-to-an-expert"
                target="_blank"
                variant="elevated"
            >
                Learn the basics
            </StyledButton>
        </StyledLayout>
    );
};

export default Header;
